import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {isEqual} from 'lodash';
import React, {useEffect, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {useSelector} from '../../../state/hooks';
import {usePanelComments} from '../../../state/reports/usePanelComments';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {Ref as PanelBankSectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {getDndSortablePanelId} from '../../PanelBank/utils';
import {useWorkspaceSectionContext} from '../../PanelBank/WorkspaceSectionContextProvider';
import {PanelCompRedux} from '../../PanelCompRedux';
import {useSectionSettingsContext} from '../../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from '../hooks/hooks';
import {PanelContextProvider, usePanelContext} from '../PanelContextProvider';
import {PanelEditorContainer} from '../PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../PanelIDContext';
import {SharedPanelStateContextProvider} from '../SharedPanelStateContext';
import {PanelPopupMode} from '../types';
import {WorkspacePanelHeader} from './WorkspacePanelHeader2';

type WorkspacePanelContainerProps = {
  isProjectReadOnly: boolean;
  panelBankSectionConfigRef: PanelBankSectionRef;
  panelHeight: number;
  panelRef: PanelRef;
  panelWidth: number;
};

export const WorkspacePanelContainer = ({
  isProjectReadOnly,
  panelBankSectionConfigRef,
  panelHeight,
  panelRef,
  panelWidth,
}: WorkspacePanelContainerProps) => {
  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].__id__
  );

  const {inheritedSettings} = useSectionSettingsContext();

  const {setNodeRef, transform, transition, isDragging} = useSortable({
    id: getDndSortablePanelId(panelRef),
    data: {
      // This helps indicate if we're dragging a section or panel
      type: 'panel',
      sectionRef: panelBankSectionConfigRef,
      panelRef: panelRef,
    },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        height: panelHeight,
        transform: CSS.Transform.toString(transform),
        transition,
        width: panelWidth,
        zIndex: isDragging ? 1 : 0, // THIS IS IMPORTANT - this allows the active panel to be dragged above other panels
      }}>
      <PanelContextProvider
        inheritedSettings={inheritedSettings}
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}>
        <PanelIDContextProvider stablePanelId={stablePanelId}>
          <WorkspacePanel />
        </PanelIDContextProvider>
      </PanelContextProvider>
    </div>
  );
};

export const WorkspacePanel = () => {
  const {shouldRenderContent} = useWorkspaceSectionContext();
  const {isReadOnly, panelRef, panel, setPanelPopupMode} = usePanelContext();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const panelId = searchParams.get('panelId');

  const configXBounds = useConfigXBounds(panelRef);

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  // Open panel editor only if panelId matches the current panel's ID
  useEffect(() => {
    if (panelId === panel.__id__) {
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    } else {
      setPanelPopupMode(undefined);
    }
  }, [panelId, panel.__id__, setPanelPopupMode]);

  const {isHighlighted} = usePanelComments({panelRef});

  const {isDragging, over} = useSortable({
    id: getDndSortablePanelId(panelRef),
  });

  /**
   * This is used in integration tests and a few panels.
   * This doesn't add a "-" between every space which is not what we want, so we should
   * clean this up when time allows.
   */
  const panelViewTypeClass = useMemo(
    () => panelType.toLowerCase().replace(' ', '-') + '-container',
    [panelType]
  );

  // TODO - remove when all styles are migrated to tailwind
  // const panelClassNames = useMemo(() => {
  //   return classNames('editable-panel', panelViewTypeClass, {
  //     'panel-selected': isSelected,
  //     'panel-highlighted': isHighlighted,
  //     'editable-panel-no-content': !shouldRenderContent,
  //   });
  // }, [panelViewTypeClass, isSelected, isHighlighted, shouldRenderContent]);

  const {targetPanelRef} = useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () => isEqual(panelRef, targetPanelRef),
    [panelRef, targetPanelRef]
  );
  return (
    <SharedPanelStateContextProvider config={configXBounds}>
      <PanelEditorContainer currentHeight={0} />
      <TailwindContents>
        <div
          className={twMerge(
            'flex flex-col',
            'h-full w-full',
            'px-8 pb-12 pt-4',
            'bg-white',
            'rounded-[3px] border-[1px] border-moon-300',
            'transition-[box-shadow,margin-top] duration-300',
            // Panel type specific class
            panelViewTypeClass,
            // Conditional states
            isHighlighted && 'shadow-[0px_0px_0px_4px_#FFDD80]',
            !shouldRenderContent && 'opacity-50 [&>*]:hidden',
            // Drag and workspace drawer states
            (isDragging || isWorkspaceDrawerTarget) &&
              'border-gold-350 shadow-[0px_0px_0px_4px_#FFDD80]',
            // Don't show crosshair tooltip on panels while dragging or resizing
            isDragging || over ? '!pointer-events-none' : 'cursor-default'
          )}
          data-test="panel">
          <WorkspacePanelHeader />
          <div
            className={twMerge(
              'flex flex-col',
              'h-full w-full grow-[1]',
              'justify-center',
              // Applying this to other panels may cause infinite ResizeObserver loop. Please proceed with caution
              // if you are loosening this check.
              (panelType === 'Code Comparer' ||
                panelType === 'Media Browser' ||
                panelType === 'Markdown Panel') &&
                'overflow-auto'
            )}
            id={'editable-panel-' + panel.__id__}>
            <PanelCompRedux
              configMode={false}
              currentHeight={0}
              dimensions={undefined as any}
              panelRef={panelRef}
              readOnly={isReadOnly}
            />
          </div>
        </div>
      </TailwindContents>
    </SharedPanelStateContextProvider>
  );
};
